import { useEffect, useRef, useState } from "react";
import { api, ApiError } from "../api.js";
import { arrivedWithCode, takeEnrolCode } from "../enrolLink.js";

/**
 * Shown when the device has no device cookie.
 *
 * Two ways in. **The QR**, which is how thirty phones get enrolled: the
 * phone's own camera opens `/enrol#code=…`, this reads the fragment and
 * redeems it without anybody typing anything. And **the token**, pasted
 * from an admin — 43 characters of base64, kept because it is the path
 * that needs nothing but the app itself.
 *
 * The code arrives in the **fragment**, never the query string. A
 * fragment is not sent to the server, so it stays out of the request log
 * — Fastify logs `req.url`, and the logger redacts cookies but not URLs.
 *
 * Taking it out of the address bar is **not** done here, though it was
 * at first. See `enrolLink.ts`: this screen only mounts when the device
 * is unregistered, so a code opened in an already-registered browser was
 * left sitting in the URL. It is stripped at boot instead, and this
 * screen asks for what was found.
 */
export function EnrolScreen({ onEnrolled }: { onEnrolled: () => void }) {
  const [token, setToken] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  // Whether a code was found in the URL at all. Until that is known the
  // screen must not offer the paste box: on a phone that has just
  // scanned, a text field asking for a token is the wrong thing to
  // flash up for a moment.
  const [fromCode, setFromCode] = useState(arrivedWithCode);
  // Effects run twice under StrictMode in development, and a code is
  // single-use — but takeEnrolCode already consumes it, so the second
  // run finds nothing. This is the belt to that pair of braces.
  const redeemed = useRef(false);

  useEffect(() => {
    if (redeemed.current) return;
    const code = takeEnrolCode();
    if (!code) {
      setFromCode(false);
      return;
    }
    redeemed.current = true;

    setBusy(true);
    api
      .enrolWithCode(code)
      .then(onEnrolled)
      .catch((err) => {
        // Deliberately not "wrong code". The common failure is a code
        // older than a minute, and the fix for that is the admin
        // tapping again — which is worth saying, because the alternative
        // is somebody assuming the phone is broken.
        //
        // But only a refused code is a stale one. Until 2026-09-26 a
        // dropped connection or a rate limit said "expired" too, and a
        // phone scanning during a deploy was sent for a new code when
        // the one it had was fine.
        const status = err instanceof ApiError ? err.status : 0;
        setError(
          status === 401 || status === 409
            ? "That code has expired or has already been used. Ask for a new one."
            : status === 429
              ? "Too many tries from here. Wait a few minutes, then scan the code again."
              : "The server did not answer. Check the connection, then scan the code again.",
        );
        setFromCode(false);
        setBusy(false);
      });
  }, [onEnrolled]);

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await api.enrol(token.trim());
      onEnrolled();
    } catch (err) {
      setError(
        err instanceof ApiError && err.status === 401
          ? "This device is not registered. Check the token."
          : "Could not register. Check the connection.",
      );
      setBusy(false);
    }
  };

  if (fromCode) {
    return (
      <main className="centred">
        <h1>Registering this device</h1>
        <p className="muted">One moment.</p>
      </main>
    );
  }

  return (
    <main className="centred">
      <h1>Register this device</h1>
      <p className="muted">
        This device is not registered yet. Scan the code an admin shows
        you, or paste the token from them.
      </p>

      <form onSubmit={submit} className="stack">
        <div className="enrol-row">
          {/* Decorative: the heading already says what this screen is,
              so alt="" keeps a screen reader from announcing the shop's
              name twice. */}
          <img src="/walts-logo.png" alt="" className="brandmark" />
          <input
            type="text"
            value={token}
            onChange={(e) => setToken(e.target.value)}
            placeholder="Device token"
            autoComplete="off"
            autoCapitalize="off"
            spellCheck={false}
            disabled={busy}
            aria-label="Device token"
          />
        </div>
        {error ? <p className="error" role="alert">{error}</p> : null}
        <button type="submit" className="primary" disabled={busy || token.trim().length < 16}>
          {busy ? "Registering…" : "Register"}
        </button>
      </form>
    </main>
  );
}
